<!-- Desktop Cart (hidden on mobile) -->
<div class="cart cart--desktop">
  <!-- Header -->
  <div class="cart__header">
    <mat-icon class="cart__header-icon">event_note</mat-icon>
    <span class="cart__header-title">{{ headerTitle() }}</span>
  </div>

  @if (warningMessage()) {
    <div class="cart__warning">
      <mat-icon class="cart__warning-icon">info</mat-icon>
      <span class="cart__warning-text">{{ warningMessage() }}</span>
    </div>
  }

  <!-- Scheduled date/time (Summary mode) -->
  @if (scheduledDate()) {
    <div class="cart__schedule">
      <div class="cart__schedule-row">
        <mat-icon class="cart__schedule-icon">calendar_today</mat-icon>
        <div class="cart__schedule-info">
          <span class="cart__schedule-date">{{ scheduledDate() }}</span>
          @if (scheduledTime()) {
            <span class="cart__schedule-time">{{ scheduledTime() }}</span>
          }
        </div>
      </div>
    </div>
  }

  <!-- Items list or empty state -->
  <div class="cart__body">
    @if (isEmpty()) {
      <div class="cart__empty">
        <mat-icon class="cart__empty-icon">event_note</mat-icon>
        <p class="cart__empty-text">Choose a service to plan your appointment</p>
      </div>
    } @else {
      <div class="cart__items">
        @for (item of items(); track $index; let i = $index) {
          <div class="cart__item">
            <div class="cart__item-thumb">
              @if (item.imageUrl) {
                <img
                  [src]="item.imageUrl"
                  [alt]="item.name"
                  class="cart__item-image"
                />
              } @else {
                <div class="cart__item-initials">
                  {{ item.initials || item.name?.charAt(0) }}
                </div>
              }
            </div>
            <div class="cart__item-details">
              <span class="cart__item-name">{{ item.name }}</span>
              @if (item.providerName) {
                <span class="cart__item-provider">{{ item.providerName }}</span>
              }
              <span class="cart__item-meta">
                {{ item.duration }} min
              </span>
            </div>
            @if (!readOnly()) {
            <button
              class="cart__item-remove"
              (click)="onRemoveItem(i)"
              aria-label="Remove item"
            >
              <mat-icon>cancel</mat-icon>
            </button>
            }
          </div>
        }
      </div>

    }
  </div>

  @if (showMakeChangesLink()) {
    <div class="cart__make-changes">
      <button class="cart__make-changes-btn" (click)="makeChangesClick.emit()">
        <mat-icon class="cart__make-changes-icon">edit</mat-icon>
        <span>Need to make changes?</span>
      </button>
    </div>
  }

  <!-- Totals -->
  <div class="cart__totals">
    <div class="cart__totals-row">
      <div class="cart__total-duration">
        <span class="cart__total-label">TOTAL DURATION</span>
        <span class="cart__total-value">
          {{ totalHours() }} hr {{ totalMinutes() }} min
        </span>
      </div>
    </div>
  </div>

  <!-- CTA Button -->
  <div class="cart__cta">
    <button
      mat-flat-button
      color="primary"
      class="cart__cta-button"
      [disabled]="ctaDisabled()"
      (click)="onCtaClick()"
    >
      {{ ctaLabel() }}
      <mat-icon class="cart__cta-arrow" iconPositionEnd>arrow_right</mat-icon>
    </button>

    @if (showBackButton()) {
      <button
        mat-stroked-button
        class="cart__back-button"
        (click)="onBackClick()"
      >
        <mat-icon class="cart__back-arrow">arrow_left</mat-icon>
        {{ backLabel() }}
      </button>
    }
  </div>
</div>

<!-- Mobile Sticky Bottom Bar (hidden on desktop) -->
<div class="cart-mobile" [class.cart-mobile--expanded]="mobileExpanded()">
  <!-- Expandable detail panel -->
  @if (mobileExpanded()) {
    <div class="cart-mobile__detail">
      <div class="cart-mobile__detail-header">
        <mat-icon class="cart-mobile__detail-header-icon">event_note</mat-icon>
        <span class="cart-mobile__detail-title">{{ headerTitle() }}</span>
      </div>

      <!-- Scheduled date/time (Summary mode) -->
      @if (scheduledDate()) {
        <div class="cart-mobile__schedule">
          <div class="cart-mobile__schedule-row">
            <mat-icon class="cart-mobile__schedule-icon">calendar_today</mat-icon>
            <div class="cart-mobile__schedule-info">
              <span class="cart-mobile__schedule-date">{{ scheduledDate() }}</span>
              @if (scheduledTime()) {
                <span class="cart-mobile__schedule-time">{{ scheduledTime() }}</span>
              }
            </div>
          </div>
        </div>
      }

      @if (!isEmpty()) {
        <div class="cart-mobile__items">
          @for (item of items(); track $index; let i = $index) {
            <div class="cart-mobile__item">
              <div class="cart-mobile__item-thumb">
                @if (item.imageUrl) {
                  <img
                    [src]="item.imageUrl"
                    [alt]="item.name"
                    class="cart-mobile__item-image"
                  />
                } @else {
                  <div class="cart-mobile__item-initials">
                    {{ item.initials || item.name?.charAt(0) }}
                  </div>
                }
              </div>
              <div class="cart-mobile__item-details">
                <span class="cart-mobile__item-name">{{ item.name }}</span>
                @if (item.providerName) {
                  <span class="cart-mobile__item-provider">{{ item.providerName }}</span>
                }
                <span class="cart-mobile__item-meta">{{ item.duration }} min</span>
              </div>
              @if (!readOnly()) {
              <button
                class="cart-mobile__item-remove"
                (click)="onRemoveItem(i)"
                aria-label="Remove item"
              >
                <mat-icon>cancel</mat-icon>
              </button>
              }
            </div>
          }
        </div>
      }

      @if (showMakeChangesLink()) {
        <div class="cart-mobile__make-changes">
          <button class="cart-mobile__make-changes-btn" (click)="makeChangesClick.emit()">
            <mat-icon class="cart-mobile__make-changes-icon">edit</mat-icon>
            <span>Need to make changes?</span>
          </button>
        </div>
      }
    </div>
  }

  <!-- Summary row (always visible) -->
  <div class="cart-mobile__summary" (click)="toggleMobileExpand()">
    <div class="cart-mobile__summary-text">
      <span class="cart-mobile__summary-count">{{ items().length }} service{{ items().length !== 1 ? 's' : '' }}</span>
      <span class="cart-mobile__summary-duration">{{ totalDurationMinutes() }} min</span>
    </div>
    <div class="cart-mobile__summary-chevron-box">
      <mat-icon class="cart-mobile__summary-chevron">
        {{ mobileExpanded() ? 'expand_more' : 'expand_less' }}
      </mat-icon>
    </div>
  </div>

  <!-- Action buttons row -->
  <div class="cart-mobile__actions">
    @if (showBackButton()) {
      <button
        mat-stroked-button
        class="cart-mobile__back-btn"
        (click)="onBackClick()"
      >
        <mat-icon class="cart-mobile__back-arrow">arrow_left</mat-icon>
        {{ backLabel() }}
      </button>
    }
    <button
      mat-flat-button
      color="primary"
      class="cart-mobile__cta-btn"
      [class.cart-mobile__cta-btn--full]="!showBackButton()"
      [disabled]="ctaDisabled()"
      (click)="onCtaClick()"
    >
      {{ mobileCtaLabel() || ctaLabel() }}
      <mat-icon class="cart-mobile__cta-arrow" iconPositionEnd>arrow_right</mat-icon>
    </button>
  </div>
</div>
